import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import moment from "moment";
import customAxios from "../../../../utils/customAxios";
import { getAvailableVehicle } from "../../../../actions/admin/booking";
import { normalizeLocation } from "../utils";
import { checkVehicleCapacity } from "../../../../utils/vehicleCapacity";
import { useBookingEditText } from "../text";

// True when the selected vehicles cannot carry everyone and their luggage.
// Uses the vehicle search's own rule (see utils/vehicleCapacity.js): luggage
// room depends on how many passengers ride in each vehicle, so a vehicle the
// search offered for this booking is never flagged here.
const isSelectedVehicleCapacityExceeded = ({ vehicles, vehicleTypes, passengers, luggage }) => {
    const selectedVehicleTypes = vehicles
        .map(vehicle => vehicleTypes.find(vt => vt.typename === vehicle.selectedVehicle))
        .filter(Boolean);
    const totalPassengers =
        Number(passengers?.adult || 0) +
        Number(passengers?.child || 0) +
        Number(passengers?.toddler || 0) +
        Number(passengers?.baby || 0);
    const { canCarry } = checkVehicleCapacity(selectedVehicleTypes, totalPassengers, {
        checkinSize: luggage?.checkin,
        cabinSize: luggage?.cabin,
    });
    return !canCarry;
};

// ─── useVehicles ───────────────────────────────────────────────────

export function useVehicles({
    transferType,
    pickupLocationData,
    dropoffLocationData,
    airportDropoffLocations,
    formatedTravelDate,
    travelDate,
    pickupTime,
    passengers,
    luggage,
    duration,
    travelArea,
    transferFlags,
    lang,
    role = "individual"
}) {
    const { t } = useBookingEditText();
    const [vehicles, setVehicles] = useState([
        { id: 1, selectedVehicle: "Toyota Alphard" },
    ]);
    const [isInvalidVehicle, setIsInvalidVehicle] = useState({ isInvalid: false, type: null });
    const [availableVehicleTypes, setAvailableVehicleTypes] = useState([]);
    const [allAvailableVehicleTypes, setAllAvailableVehicleTypes] = useState([]);
    const [finalAvailableVehicleTypes, setFinalAvailableVehicleTypes] = useState([]);
    const [isVehicleLoading, setIsVehicleLoading] = useState(false);
    const debounceTimeoutRef = useRef(null);

    useEffect(() => {
        if(role === "business"){
            setFinalAvailableVehicleTypes(allAvailableVehicleTypes);
        }else{
            if(vehicles.length === 1){
                setFinalAvailableVehicleTypes(availableVehicleTypes);
            }else{
                setFinalAvailableVehicleTypes(allAvailableVehicleTypes);
            }
        }
    }, [availableVehicleTypes, allAvailableVehicleTypes, vehicles]);

    const vehicleOptions = useMemo(
        () =>
            finalAvailableVehicleTypes.map((vehicleType) => ({
                label: vehicleType?.typename || "",
                value: vehicleType?.typename || "",
            })),
        [finalAvailableVehicleTypes]
    );

    const handleChangeVehicle = (id, selectedValue) => {
        setVehicles((prevVehicles) =>
            prevVehicles.map((vehicle) =>
                vehicle.id === id
                    ? { ...vehicle, selectedVehicle: selectedValue }
                    : vehicle
            )
        );
    };

    const handleAddVehicle = () => {
        setVehicles((prevVehicles) => {
            const nextId = prevVehicles.length
                ? Math.max(...prevVehicles.map((vehicle) => vehicle.id)) + 1
                : 1;
            return [...prevVehicles, { id: nextId, selectedVehicle: "" }];
        });
    };

    const handleRemoveVehicle = (id) => {
        setVehicles((prevVehicles) => {
            if (prevVehicles.length <= 1) return prevVehicles;
            return prevVehicles.filter((vehicle) => vehicle.id !== id);
        });
    };

    const formatVehicle = (selectedVehicleName) => {
        if (!selectedVehicleName) return null;
        return vehicleOptions.find((option) => option.value === selectedVehicleName) || null;
    };

    const renderVehicleCard = (vehicleName) => {
        if (!vehicleName) return null;

        const selectedVehicle = finalAvailableVehicleTypes.find(
            (vehicleType) => vehicleType?.typename === vehicleName
        );

        if (!selectedVehicle) return null;

        const vehicleImage = selectedVehicle?.images?.png || "-";

        return (
            <div className="tw:flex tw:flex-col tw:gap-4 tw:rounded-[5px] tw:bg-[#f9f9f9] tw:p-4 tw:w-full">
                <img
                    src={vehicleImage}
                    alt={vehicleName}
                    className="tw:h-[120px] tw:w-[226px] tw:self-center tw:object-contain"
                />
                <div className="tw:h-px tw:w-full tw:bg-[#e0e0e0]" />
                <div>
                    <div className="tw:mb-1 tw:text-[10px] tw:font-bold tw:text-[#a7a7a7]">
                        {t("editBooking.BookingEditPage.vehicleSelection.detail.title")}
                    </div>
                    <div className="tw:m-0 tw:text-[#282828] tw:text-[13px]">
                        {t("editBooking.BookingEditPage.vehicleSelection.detail.details", {
                            baseprice: selectedVehicle?.baseprice?.toLocaleString() || 0,
                            maxPax: selectedVehicle?.maxpassenger || 0,
                            maxLuggage: selectedVehicle?.maxluggages || 0,
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Fetch available vehicles
    useEffect(() => {
        // Clear previous debounce
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        const fetchAvailableVehicles = async () => {
            const pickupLocationNormalized = normalizeLocation(pickupLocationData);
            const dropoffLocationDataList =
                transferType === "charter"
                    ? [dropoffLocationData]
                    : airportDropoffLocations.map((dropoff) => dropoff?.data).filter(Boolean);
            const dropoffLocationsNormalized = dropoffLocationDataList
                .map((location) => normalizeLocation(location))
                .filter(Boolean);

            const pickupTime24 = pickupTime
                ? moment(pickupTime, ["h:mm A", "HH:mm"], true).format("HH:mm")
                : "";
            const travelDateValue =
                formatedTravelDate || (travelDate ? moment(travelDate).format("YYYY-MM-DD") : "");

            const hasValidPax =
                Number(passengers?.adult || 0) +
                Number(passengers?.child || 0) +
                Number(passengers?.toddler || 0) +
                Number(passengers?.baby || 0) >
                0;

            if (
                !pickupLocationNormalized ||
                dropoffLocationsNormalized.length === 0 ||
                !travelDateValue ||
                !pickupTime24 ||
                !hasValidPax
            ) {
                setIsVehicleLoading(false);
                setAvailableVehicleTypes([]);
                setAllAvailableVehicleTypes([]);
                return;
            }

            const requestBody = {
                pickupLocation: pickupLocationNormalized,
                dropoffLocations: dropoffLocationsNormalized,
                bookingType: transferType === "charter" ? "byTheHour" : "specifiedDestination",
                travelDate: travelDateValue,
                pickupTime: pickupTime24,
                pax: {
                    adult: Number(passengers?.adult || 0),
                    child: Number(passengers?.child || 0),
                    toddler: Number(passengers?.toddler || 0),
                    baby: Number(passengers?.baby || 0),
                },
                luggage: {
                    checkinSize: Number(luggage?.checkin || 0),
                    cabinSize: Number(luggage?.cabin || 0),
                },
                isAirportTransfer: Boolean(transferFlags?.isAirportTransfer),
                isFromAirport: Boolean(transferFlags?.isFromAirport),
                isToAirport: Boolean(transferFlags?.isToAirport),
                isStationTransfer: Boolean(transferFlags?.isStationTransfer),
                isFromStation: Boolean(transferFlags?.isFromStation),
                isToStation: Boolean(transferFlags?.isToStation),
                returnAllAvailableVehicle: true, // Always return all available vehicles for better user experience
                useMinimumTime: false
            };

            if (transferType === "charter") {
                requestBody.usageDuration = duration?.value || "";
                requestBody.travelArea = travelArea || null;
            }

            try {
                setIsVehicleLoading(true);

                const availableVehicleResponse = await getAvailableVehicle(requestBody);
                const fetchedVehicleTypes = Array.isArray(
                    availableVehicleResponse?.data?.availableVehicleTypes
                )
                    ? [...availableVehicleResponse.data.availableVehicleTypes].sort(
                        (a, b) => (a?.baseprice || 0) - (b?.baseprice || 0)
                    )
                    : [];

                const fetchedAllVehicleTypes = Array.isArray(
                    availableVehicleResponse?.data?.allAvailableVehicleTypes
                )
                    ? [...availableVehicleResponse.data.allAvailableVehicleTypes].sort(
                        (a, b) => (a?.baseprice || 0) - (b?.baseprice || 0)
                    )
                    : [];

                setAvailableVehicleTypes(fetchedVehicleTypes);
                setAllAvailableVehicleTypes(fetchedAllVehicleTypes);
            } catch (error) {
                console.error("Error loading available vehicle list:", error);
                setAvailableVehicleTypes([]);
                setAllAvailableVehicleTypes([])
            } finally {
                setIsVehicleLoading(false);
            }
        };

        // Debounce the fetch
        debounceTimeoutRef.current = setTimeout(fetchAvailableVehicles, 500); // 500ms debounce

        // Cleanup on unmount or dependency change
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [
        transferType,
        pickupLocationData,
        dropoffLocationData,
        airportDropoffLocations,
        formatedTravelDate,
        travelDate,
        pickupTime,
        passengers,
        luggage,
        duration,
        travelArea,
        transferFlags,
    ]);

    // Sync vehicle options validity
    useEffect(() => {
        setVehicles((prevVehicles) => {
            if (prevVehicles.length === 0) {
                return [{ id: 1, selectedVehicle: "" }];
            }

            let hasChanged = false;
            const nextVehicles = prevVehicles.map((vehicle) => {
                if (!vehicle.selectedVehicle) return vehicle;

                const hasValidSelection = vehicleOptions.some(
                    (option) => option.value === vehicle.selectedVehicle
                );

                if (hasValidSelection) return vehicle;

                hasChanged = true;
                return { ...vehicle, selectedVehicle: "" };
            });

            return hasChanged ? nextVehicles : prevVehicles;
        });
    }, [vehicleOptions]);

    // Validate vehicle capacity
    useEffect(() => {
        const allVehiclesSelected = vehicles.every(vehicle => vehicle.selectedVehicle);
        if (!allVehiclesSelected) {
            setIsInvalidVehicle({ isInvalid: false, type: null });
            return;
        }

        setIsInvalidVehicle(isSelectedVehicleCapacityExceeded({ vehicles, vehicleTypes: finalAvailableVehicleTypes, passengers, luggage }) ? { isInvalid: true, type: "capacity" } : { isInvalid: false, type: null });
    }, [vehicles, passengers, luggage, finalAvailableVehicleTypes]);

    const vehicleValidation = () => {
        const allVehiclesSelected = vehicles.every(vehicle => vehicle.selectedVehicle);

        if (!allVehiclesSelected) {
            setIsInvalidVehicle({ isInvalid: true, type: "unselected" });
            return false;
        }
        if (isSelectedVehicleCapacityExceeded({ vehicles, vehicleTypes: finalAvailableVehicleTypes, passengers, luggage })) {
            setIsInvalidVehicle({ isInvalid: true, type: "capacity" });
            return false;
        }

        setIsInvalidVehicle({ isInvalid: false, type: null });
        return true;
    };

    return {
        vehicles,
        setVehicles,
        availableVehicleTypes: finalAvailableVehicleTypes,
        isVehicleLoading,
        vehicleOptions,
        handleChangeVehicle,
        handleAddVehicle,
        handleRemoveVehicle,
        formatVehicle,
        renderVehicleCard,
        isInvalidVehicle,
        vehicleValidation
    };
}
